"use client";

import React from "react";
import { Minus, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

export const CAMPAIGN_CODE_MAX_LENGTH = 25;

/** Letters (upper/lower case), digits and special characters (printable ASCII), at most 25 characters. Spaces are
 * allowed inside the code; leading and trailing ones are trimmed off before it is validated and sent. */
export const isValidCampaignCode = (code: string): boolean => {
  const trimmed = code.trim();
  return trimmed.length <= CAMPAIGN_CODE_MAX_LENGTH && /^[\x20-\x7E]*$/.test(trimmed);
};

/** Query parameters (search or hash) a promo code can be handed over in. */
const CAMPAIGN_CODE_URL_PARAMS = ["promoCode", "code", "campaignCode"];

/** The promo code passed in the URL, or "" when there is none or it is not a valid code. */
export const readCampaignCodeFromUrl = (searchParams: URLSearchParams, hashParams: URLSearchParams): string => {
  for (const params of [searchParams, hashParams]) {
    for (const key of CAMPAIGN_CODE_URL_PARAMS) {
      const code = params.get(key)?.trim();
      if (code) return isValidCampaignCode(code) ? code : "";
    }
  }
  return "";
};

interface CampaignCodeFieldProps {
  value: string;
  disabled?: boolean;
  error?: string;
  onChange: (v: string) => void;
}

/** "Code vorhanden? +" toggle that reveals the input; stays open when a code is already present. */
const CampaignCodeField: React.FC<CampaignCodeFieldProps> = ({ value, disabled = false, error, onChange }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState<boolean>(value.length > 0);
  const isOpen = expanded || value.length > 0;
  const Icon = isOpen ? Minus : Plus;

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-primary hover:opacity-80 disabled:opacity-50"
        aria-expanded={isOpen}
        aria-controls="campaignCode"
        onClick={() => {
          // An entered code must not be hidden by accident, so only an empty field can be collapsed again.
          if (value.length === 0) setExpanded(!isOpen);
        }}
        disabled={disabled}
      >
        {t("contactDetails.campaignCode")}
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
      {isOpen && (
        <>
          <Label htmlFor="campaignCode" className="sr-only">{t("contactDetails.campaignCode")}</Label>
          {/* A textarea (not an input) so the long, fixed placeholder wraps onto a second line on narrow screens. */}
          <textarea
            id="campaignCode"
            name="campaignCode"
            autoComplete="off"
            placeholder={t("contactDetails.campaignCodePlaceholder")}
            rows={1}
            maxLength={CAMPAIGN_CODE_MAX_LENGTH}
            value={value}
            // A code is a single line: strip line breaks from pasted text and don't let Enter insert one.
            onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
            disabled={disabled}
            aria-invalid={!!error}
            className="flex h-[4.25rem] w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:h-10 md:text-sm"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </>
      )}
    </div>
  );
};

export default CampaignCodeField;
